import {
  CompanyIntake,
  CompanyIntakeBusiness,
  CompanyIntakeCulture,
  CompanyIntakeGeneral,
  CompanyIntakeSales,
  CompanyIntakeTalentRecruitingHistory,
  JobIntake,
  JobIntakeCulture,
  JobIntakeDatesAndUrgency,
  JobIntakeGeneralInfo,
  JobIntakeLaborupRound,
  JobIntakeScreeningQuestions,
  JobIntakeTalentRecruitingHistory,
} from "@/types";

// ---------------------------------------------------------------------------
// Intake normalization
//
// The backend can return an intake row whose nested sub-objects/arrays are
// null or missing (or an empty "" body for the whole thing). The types
// declare those sub-objects as non-null, so downstream components read them
// directly. Normalize once at the data-entry points (query + cache-write
// hooks) so every consumer sees a complete, non-null shape.
//
// Top-level null is preserved — callers/containers still render their
// empty state when there is no intake at all.
// ---------------------------------------------------------------------------

const emptyCompanySales: CompanyIntakeSales = {
  details: null,
  dealTeam: [],
  salesOwner: null,
};

const emptyCompanyGeneral: CompanyIntakeGeneral = {
  websiteUrl: null,
  industries: null,
  headCount: null,
  whatTheyMake: null,
  rawMaterials: null,
  equipment: null,
  manufacturingProcess: null,
  maintenanceEnvironment: null,
  customers: null,
  standards: null,
  mediaLinks: [],
};

const emptyCompanyTalentRecruitingHistory: CompanyIntakeTalentRecruitingHistory =
  {
    general: {
      retention: null,
      hiringChallenges: null,
      competitors: null,
      details: null,
    },
    internal: {
      hrTeamSize: null,
      currentHrBandwidthOnRecruiting: null,
      idealHrBandwidthOnRecruiting: null,
      details: null,
    },
    external: {
      details: null,
      staffingFirmCostStructure: null,
      staffingFirmCostDetails: null,
      likesAboutExternalRecruiters: null,
      dislikesAboutExternalRecruiters: null,
    },
  };

const emptyCompanyCulture: CompanyIntakeCulture = {
  companyCultureDetails: null,
  workplaceEnvironmentDetails: null,
};

const emptyCompanyBusiness: CompanyIntakeBusiness = {
  growthStatus: null,
  headcountTrend: null,
  revenueAndHeadcountDetails: null,
  details: null,
};

function normalizeCompany(
  raw: CompanyIntake | null | undefined,
): CompanyIntake | null {
  if (!raw) return null;

  const trh = raw.talentRecruitingHistory;
  return {
    ...raw,
    stakeholders: raw.stakeholders ?? [],
    sites: raw.sites ?? [],
    attachments: raw.attachments ?? [],
    sales: raw.sales
      ? { ...emptyCompanySales, ...raw.sales, dealTeam: raw.sales.dealTeam ?? [] }
      : emptyCompanySales,
    general: raw.general
      ? { ...emptyCompanyGeneral, ...raw.general, mediaLinks: raw.general.mediaLinks ?? [] }
      : emptyCompanyGeneral,
    talentRecruitingHistory: trh
      ? {
          general: trh.general ?? emptyCompanyTalentRecruitingHistory.general,
          internal:
            trh.internal ?? emptyCompanyTalentRecruitingHistory.internal,
          external:
            trh.external ?? emptyCompanyTalentRecruitingHistory.external,
        }
      : emptyCompanyTalentRecruitingHistory,
    culture: raw.culture
      ? { ...emptyCompanyCulture, ...raw.culture }
      : emptyCompanyCulture,
    business: raw.business
      ? { ...emptyCompanyBusiness, ...raw.business }
      : emptyCompanyBusiness,
  };
}

const emptyJobTalentRecruitingHistory: JobIntakeTalentRecruitingHistory = {
  areOtherStaffingFirmsWorking: false,
  willOtherStaffingFirmsWork: false,
  isInternalTeamRecruiting: false,
};

const emptyJobLaborupRound: JobIntakeLaborupRound = {
  submissionProcess: null,
  additionalExpectations: null,
};

const emptyJobScreeningQuestions: JobIntakeScreeningQuestions = {
  dealBreakers: null,
  contextBuilders: null,
};

const emptyJobGeneralInfo: JobIntakeGeneralInfo = {};
const emptyJobCulture: JobIntakeCulture = {};
const emptyJobDatesAndUrgency: JobIntakeDatesAndUrgency = {};

function normalizeJob(raw: JobIntake | null | undefined): JobIntake | null {
  if (!raw) return null;

  return {
    ...raw,
    stakeholders: raw.stakeholders ?? [],
    idealCandidateProfiles: raw.idealCandidateProfiles ?? [],
    antiIdealCandidateProfiles: raw.antiIdealCandidateProfiles ?? [],
    interviewProcess: raw.interviewProcess ?? [],
    attachments: raw.attachments ?? [],
    generalInfo: raw.generalInfo ?? emptyJobGeneralInfo,
    culture: raw.culture ?? emptyJobCulture,
    datesAndUrgency: raw.datesAndUrgency ?? emptyJobDatesAndUrgency,
    talentRecruitingHistory: raw.talentRecruitingHistory
      ? { ...emptyJobTalentRecruitingHistory, ...raw.talentRecruitingHistory }
      : emptyJobTalentRecruitingHistory,
    laborupRound: raw.laborupRound
      ? { ...emptyJobLaborupRound, ...raw.laborupRound }
      : emptyJobLaborupRound,
    screeningQuestions: raw.screeningQuestions
      ? { ...emptyJobScreeningQuestions, ...raw.screeningQuestions }
      : emptyJobScreeningQuestions,
  };
}

export const intakeNormalize = {
  company: normalizeCompany,
  job: normalizeJob,
};
